import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The last line of defence.
 *
 * Without one of these, a single render throw gives a blank white screen with
 * no way back — on a phone, in a shop, with the list you came for sitting
 * intact in IndexedDB behind it. That is the worst possible failure for this
 * app, because the data is fine and only the view is broken.
 *
 * So the message says the one thing that matters — **nothing is lost** — and
 * offers the two ways out. Reload fixes a transient render error. Going to the
 * list fixes a screen that is broken for a specific record, which is the more
 * common case: a compare view for a product with odd data should not cost you
 * the whole app.
 *
 * It deliberately does **not** offer to clear local data. That would throw away
 * the unsent outbox, which is the one thing here that cannot be recovered from
 * the server.
 */

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // There is no error-reporting service and there should not be one for a
    // two-person household — the console is where the developer looks.
    console.error('render failed', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main className="stack" style={{ padding: 24 }}>
        <h1>Er ging iets kapot</h1>
        <p>
          Je lijst en alles wat je hebt toegevoegd staan veilig op deze telefoon — er is niets
          verloren, en wijzigingen die nog niet verstuurd zijn synchroniseren gewoon.
        </p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Opnieuw laden
        </button>
        <button
          className="btn"
          onClick={() => {
            // Clearing the error before navigating, so returning to a working
            // screen actually renders instead of showing this again.
            this.setState({ error: null });
            window.location.assign('/');
          }}
        >
          Terug naar de lijst
        </button>
        <p className="row-sub">{error.message}</p>
      </main>
    );
  }
}
